import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { User, UserRole } from '../types';
import { tokenManager, initSecurity, getSecureHeaders } from '../utils/security';
import { snakeToCamel, camelToSnake } from '../utils/caseTransform';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<User | null>;
  register: (data: RegisterData) => Promise<boolean | string>;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
  verifyEmail: (code: string) => Promise<boolean>;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  preferredLanguage: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Initialize security measures and check session on mount
  useEffect(() => {
    initSecurity();
    checkSession();
  }, []);

  const checkSession = async () => {
    let accessToken = tokenManager.getAccessToken();

    // No in-memory token (e.g. after a page refresh). Try to restore
    // the session using the HTTP-only refresh token cookie.
    if (!accessToken) {
      const refreshed = await tokenManager.refreshAccessToken();
      if (refreshed) {
        accessToken = tokenManager.getAccessToken();
      }
    }

    if (!accessToken) {
      setState((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    try {
      const response = await fetch('/api/auth/session', {
        credentials: 'include',
        headers: { ...getSecureHeaders(), 'Authorization': `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const raw = await response.json();
        const data = snakeToCamel<{ user?: User }>(raw);
        if (data.user) {
          setState({ user: data.user, isAuthenticated: true, isLoading: false });
          return;
        }
      }
    } catch {
      // Session check failed, user not authenticated
    }
    tokenManager.clearTokens();
    setState((prev) => ({ ...prev, isLoading: false }));
  };

  const login = useCallback(async (email: string, password: string): Promise<User | null> => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: getSecureHeaders(),
        credentials: 'include',
        body: JSON.stringify(camelToSnake({ email, password })),
      });

      if (response.ok) {
        const raw = await response.json();
        const data = snakeToCamel<{ accessToken: string; expiresIn: number; user: User | null }>(
          raw,
        );
        tokenManager.setTokens(data.accessToken, data.expiresIn);
        setState({ user: data.user, isAuthenticated: true, isLoading: false });
        return data.user ?? null;
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  const register = useCallback(async (data: RegisterData): Promise<boolean | string> => {
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: getSecureHeaders(),
        credentials: 'include',
        body: JSON.stringify(camelToSnake(data)),
      });

      if (response.ok) {
        const raw = await response.json();
        const result = snakeToCamel<{ accessToken: string; expiresIn: number; user: User | null }>(
          raw,
        );
        tokenManager.setTokens(result.accessToken, result.expiresIn);
        setState({ user: result.user, isAuthenticated: true, isLoading: false });
        return true;
      }
      const err = await response.json().catch(() => null);
      return err?.detail || 'Registration failed. Please try again.';
    } catch {
      return 'An error occurred. Please try again.';
    }
  }, []);

  const logout = useCallback(() => {
    const accessToken = tokenManager.getAccessToken();
    tokenManager.clearTokens();
    fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...getSecureHeaders(),
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify({}),
    }).catch(() => {});
    setState({ user: null, isAuthenticated: false, isLoading: false });
  }, []);

  const updateUser = useCallback((updates: Partial<User>) => {
    setState((prev) => ({
      ...prev,
      user: prev.user ? { ...prev.user, ...updates } : null,
    }));
  }, []);

  const verifyEmail = useCallback(async (code: string): Promise<boolean> => {
    try {
      const response = await fetch('/api/auth/verify-email', {
        method: 'POST',
        headers: getSecureHeaders(),
        credentials: 'include',
        body: JSON.stringify({ code }),
      });
      if (response.ok) {
        updateUser({ emailVerified: true });
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [updateUser]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        register,
        logout,
        updateUser,
        verifyEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
